/**
 * In-memory state of zero-knowledge vault.
 *
 * Holds unlocked account (`accountKey`) and personal vault key
 * IN-MEMORY ONLY (never serialized). Derives UI state machine from:
 * bloqueo combinando:
 *   - whether local `vault_account` row exists (→ setup already done), and
 *   - whether keys are in memory (→ unlocked).
 *
 * Inactivity auto-lock + manual lock clear keys.
 */

import {
  type Accessor,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  type ParentComponent,
  useContext,
} from "solid-js";
import {
  and,
  desc,
  eq,
  isNull,
  type PowerSyncDrizzleDB,
  vault_account as vaultAccountTable,
  type VaultAttachmentRecord,
  type VaultItemRecord,
  vault as vaultTable,
} from "@boveda/powersync";
import {
  type AttachmentMeta,
  createPersonalVaultMutation,
  createVaultAccountMutation,
  createVaultItemMutation,
  crypto,
  deleteVaultAttachmentMutation,
  deleteVaultItemMutation,
  generateId,
  getMyVaultMembershipQuery,
  insertVaultAttachmentMutation,
  nowISO,
  openMembershipRow,
  type ProtectedAccount,
  type ProtectedAttachment,
  queryKeys,
  restoreVaultItemMutation,
  type UnlockedAccount,
  updateVaultItemMutation,
  type VaultItemPlain,
  type VaultMembershipRow,
} from "@boveda/shared";
import { createPowerSyncQuery } from "@boveda/shared/solid";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import { holdSettledStatus, type SettledVaultStatus } from "./vault-state";

export type VaultStatus = "loading" | "needs-setup" | "locked" | "unlocked";

/** Decrypted item, ready to render (with id/rev for editing). */
export interface DecryptedVaultItem {
  readonly id: string;
  readonly rev: number;
  readonly plain: VaultItemPlain;
}

interface VaultAccountRow {
  id: string;
  user_id: string | null;
  kdf: string | null;
  protected_account_key: string | null;
  protected_private_key: string | null;
  public_key: string | null;
  auth_hash: string | null;
  recovery_blob: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface VaultRow {
  id: string;
  protected_key: string | null;
}

/** Organization vault key opened from MY membership. */
export interface OpenedOrgVault {
  readonly key: Uint8Array;
  readonly epoch: number;
  readonly role: "owner" | "admin" | "member";
}

interface VaultContextValue {
  status: Accessor<VaultStatus>;
  userId: Accessor<string>;
  vaultId: Accessor<string | null>;
  /**
   * Unlocked account (keys IN-MEMORY ONLY). Needed by org-vault /
   * sharing flows (private key to open memberships/envelopes). Never
   * serializar.
   */
  account: Accessor<UnlockedAccount | null>;
  /** Creates zero-knowledge account + personal vault and unlocks. */
  setup: (password: string) => Promise<void>;
  /** Unlocks with master password. Throws Error if incorrect. */
  unlock: (password: string) => Promise<void>;
  /** Manual lock: clears keys from memory. */
  lock: () => void;
  /** Decrypts item row in memory. */
  decryptRow: (row: VaultItemRecord) => Promise<DecryptedVaultItem>;
  /** Encrypts and persists item (creates if no id; increments rev on edit). */
  saveItem: (plain: VaultItemPlain, existing?: { id: string; rev: number }) => Promise<string>;
  /**
   * Opens key of an ORGANIZATION vault from my synchronized membership
   * (sealed to my public key). Caches by epoch: rotation (new epoch)
   * forces re-opening. Throws if no membership (revoked / pending sync).
   */
  openOrgVault: (vaultId: string) => Promise<OpenedOrgVault>;
  /** Decrypts item row with explicit vault key (org). */
  decryptRowWith: (key: Uint8Array, row: VaultItemRecord) => Promise<DecryptedVaultItem>;
  /** Encrypts and persists item in specific vault with its key (org). */
  saveItemWith: (
    targetVaultId: string,
    key: Uint8Array,
    plain: VaultItemPlain,
    existing?: { id: string; rev: number },
  ) => Promise<string>;
  /** `vaultId`: vault (personal or org) that `id` belongs to — see `scope` note below. */
  deleteItem: (vaultId: string, id: string) => Promise<void>;
  /** `vaultId`: vault (personal or org) that `id` belongs to — see `scope` note below. */
  restoreItem: (vaultId: string, id: string) => Promise<void>;
  /**
   * Attaches encrypted file to PERSONAL vault item. Encrypts bytes on
   * client (per-attachment content key) and persists only ciphertext.
   */
  attachFile: (
    itemId: string,
    filename: string,
    mime: string,
    bytes: Uint8Array,
  ) => Promise<string>;
  /** Downloads+decrypts PERSONAL vault attachment (plaintext bytes + metadata). */
  openAttachment: (row: VaultAttachmentRecord) => Promise<crypto.DecryptedAttachment>;
  /**
   * `vaultId`: the vault (personal or organization) the attachment
   * belongs to. Forwarded as `scope` of `patchRowVerified` — `vault_item` /
   * `vault_attachment` have no `organization_id`, so `vault_id`
   * IS the row-level tenant boundary (§4.14 of `infra/PENDING_WORK.md`;
   * unconstrained `id` without `vault_id` could reach a row of ANOTHER
   * org vault of same user, since `org_vaults` synchronizes
   * all user org vaults, not only active one).
   */
  deleteAttachment: (vaultId: string, id: string) => Promise<void>;
  /** Resets auto-lock timer (called on user activity). */
  keepAlive: () => void;
  /** Minutes of inactivity before auto-lock. */
  autoLockMinutes: Accessor<number>;
  setAutoLockMinutes: (n: number) => void;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
}

const VaultContext = createContext<VaultContextValue>();

const DEFAULT_AUTO_LOCK_MINUTES = 5;
const MS_PER_MINUTE = 60_000;

export const VaultProvider: ParentComponent = (props) => {
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();
  const session = useSession();
  const userId = () => session.userId();

  // Keys in memory (never serialized).
  const [unlocked, setUnlocked] = createSignal<UnlockedAccount | null>(null);
  const [vaultKey, setVaultKey] = createSignal<Uint8Array | null>(null);
  const [vaultId, setVaultId] = createSignal<string | null>(null);
  const [autoLockMinutes, setAutoLockMinutes] = createSignal(DEFAULT_AUTO_LOCK_MINUTES);
  // Opened ORGANIZATION vault keys (by vaultId, with epoch).
  // Memory only; zeroized on lock().
  const orgKeys = new Map<string, OpenedOrgVault>();

  // Does local account exist? (reactive over PowerSync)
  const accountQuery = createPowerSyncQuery<VaultAccountRow>({
    queryKey: () => queryKeys.vault.account(userId()),
    queryFn: () => {
      const d = drizzle();
      return d
        ? d.select().from(vaultAccountTable).where(eq(vaultAccountTable.user_id, userId())).limit(1)
        : null;
    },
    db,
    enabled: () => Boolean(drizzle()) && Boolean(userId()),
  });

  const accountRow = createMemo<VaultAccountRow | null>(() => accountQuery.data?.[0] ?? null);

  // Last settled gate state, so a transient "loading" of the same user does
  // not unmount the setup form (see `holdSettledStatus`). Plain variable: it
  // only feeds the memo below and never needs to notify anything.
  let settledStatus: SettledVaultStatus | null = null;

  const status = createMemo<VaultStatus>(() => {
    // In-memory keys of ACTIVE account rule: once created/unlocked for this
    // session, transient reload of `accountQuery` MUST NOT bounce vault back
    // to "loading" and re-close UI. Happened right after new REGISTRATION:
    // `vault_account` write re-triggers query over freshly bound per-user DB
    // (bind churn, CC-29 class), leaving it in
    // `isLoading` while keys were in memory → setup never reached
    // "unlocked". Requires `unlocked().userId === userId()` so an unlocked
    // vault from prior account is NEVER displayed under another.
    const acc = unlocked();
    if (acc && acc.userId === userId() && vaultKey()) {
      return "unlocked";
    }
    const derived: VaultStatus = drizzle()
      ? accountQuery.isLoading
        ? "loading"
        : accountRow()
          ? "locked"
          : "needs-setup"
      : "loading";
    const held = holdSettledStatus(derived, userId(), settledStatus);
    settledStatus = held.settled;
    return held.status;
  });

  // ── Inactivity auto-lock ─────────────────────────────────────────────────
  const [lastActivity, setLastActivity] = createSignal(Date.now());
  const keepAlive = () => setLastActivity(Date.now());

  createEffect(() => {
    if (status() !== "unlocked") {
      return;
    }
    const intervalMs = 15_000;
    const timer = setInterval(() => {
      const idleMs = Date.now() - lastActivity();
      if (idleMs >= autoLockMinutes() * MS_PER_MINUTE) {
        lock();
      }
    }, intervalMs);
    onCleanup(() => {
      clearInterval(timer);
    });
  });

  function lock(): void {
    // Zero keys before discarding.
    const key = vaultKey();
    if (key) {
      key.fill(0);
    }
    const acc = unlocked();
    if (acc) {
      acc.accountKey.fill(0);
    }
    for (const opened of orgKeys.values()) {
      opened.key.fill(0);
    }
    orgKeys.clear();
    setVaultKey(null);
    setUnlocked(null);
  }

  onCleanup(() => {
    lock();
  });

  function rowToProtectedAccount(row: VaultAccountRow): ProtectedAccount {
    // KDF JSON was written by this app (`createVaultAccountMutation` serializes
    // a KdfConfig), so shape is trusted; assertion is inevitable
    // when hydrating from SQLite (text column).
    let parsedKdf: unknown = JSON.parse(row.kdf ?? "{}");
    if (typeof parsedKdf === "string") {
      // Row touched by server with double-encoding bug (JSONB
      // of JSON string): first parse returns string. Re-parse it.
      parsedKdf = JSON.parse(parsedKdf);
    }
    return {
      userId: row.user_id ?? row.id,
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      kdf: parsedKdf as ProtectedAccount["kdf"],
      protectedAccountKey: row.protected_account_key ?? "",
      protectedPrivateKey: row.protected_private_key ?? "",
      publicKey: row.public_key ?? "",
      authHash: row.auth_hash ?? "",
    };
  }

  async function loadPersonalVault(
    dbInstance: PowerSyncDrizzleDB,
    accountKey: Uint8Array,
  ): Promise<void> {
    const rows = (await dbInstance
      .select({ id: vaultTable.id, protected_key: vaultTable.protected_key })
      .from(vaultTable)
      .where(and(eq(vaultTable.owner_user_id, userId()), isNull(vaultTable.deleted_at)))
      .orderBy(desc(vaultTable.created_at))
      .limit(1)) as VaultRow[];
    const [row] = rows;
    if (!row?.protected_key) {
      throw new Error("vault not found");
    }
    const vk = await crypto.unwrapPersonalVaultKey(accountKey, row.protected_key);
    setVaultKey(vk);
    setVaultId(row.id);
  }

  async function setup(password: string): Promise<void> {
    const dbInstance = drizzle();
    if (!dbInstance) {
      throw new Error("database not ready");
    }
    const { protectedAccount, unlocked: unlockedAccount } = await crypto.createAccount(
      userId(),
      password,
    );
    await createVaultAccountMutation(dbInstance, protectedAccount);

    const vk = crypto.createVaultKey();
    const wrapped = await crypto.wrapPersonalVaultKey(unlockedAccount.accountKey, vk);
    const id = generateId();
    await createPersonalVaultMutation(dbInstance, {
      id,
      ownerUserId: userId(),
      // MVP: "Personal" name not sensitive → stored plain. Names
      // of org vaults WILL be encrypted in name_envelope.
      nameEnvelope: "Personal",
      protectedKey: wrapped,
    });

    setUnlocked(unlockedAccount);
    setVaultKey(vk);
    setVaultId(id);
    keepAlive();
  }

  async function unlock(password: string): Promise<void> {
    const dbInstance = drizzle();
    const row = accountRow();
    if (!dbInstance || !row) {
      throw new Error("no account to unlock");
    }
    // Throws Error("invalid master password") if incorrect.
    const unlockedAccount = await crypto.unlockAccount(password, rowToProtectedAccount(row));
    await loadPersonalVault(dbInstance, unlockedAccount.accountKey);
    setUnlocked(unlockedAccount);
    keepAlive();
  }

  async function decryptRow(row: VaultItemRecord): Promise<DecryptedVaultItem> {
    const vk = vaultKey();
    if (!vk) {
      throw new Error("vault is locked");
    }
    const plain = await crypto.decryptItem(vk, {
      id: row.id,
      vaultId: row.vault_id ?? "",
      envelope: row.envelope ?? "",
      rev: row.rev ?? 0,
    });
    return { id: row.id, rev: row.rev ?? 0, plain };
  }

  async function saveItem(
    plain: VaultItemPlain,
    existing?: { id: string; rev: number },
  ): Promise<string> {
    const dbInstance = drizzle();
    const vk = vaultKey();
    const vid = vaultId();
    if (!dbInstance || !vk || !vid) {
      throw new Error("vault is locked");
    }
    const id = existing?.id ?? generateId();
    const rev = existing ? existing.rev + 1 : 1;
    const withStamp: VaultItemPlain = { ...plain, updatedAt: nowISO() };
    const protectedItem = await crypto.encryptItem(vk, vid, id, rev, withStamp);
    // Caller already knows if create or edit (`existing`): no SELECT
    // needed to decide, and avoiding it closes the ghost upsert
    // window (see `createVaultItemMutation` in
    // `@boveda/shared`).
    if (existing) {
      await updateVaultItemMutation(dbInstance, protectedItem);
    } else {
      await createVaultItemMutation(dbInstance, protectedItem);
    }
    return id;
  }

  async function openOrgVault(orgVaultId: string): Promise<OpenedOrgVault> {
    const dbInstance = drizzle();
    const acc = unlocked();
    if (!dbInstance || !acc) {
      throw new Error("vault is locked");
    }
    const rows = (await getMyVaultMembershipQuery(
      dbInstance,
      orgVaultId,
      userId(),
    )) as VaultMembershipRow[];
    const [row] = rows;
    if (!row?.sealed_vault_key) {
      // Revoked, or membership has not arrived via sync yet.
      throw new Error("no membership for this vault");
    }
    const epoch = row.key_epoch ?? 1;
    const cached = orgKeys.get(orgVaultId);
    if (cached && cached.epoch === epoch) {
      return cached;
    }
    const key = await openMembershipRow(acc.privateKey, row);
    const role = row.role === "owner" || row.role === "admin" ? row.role : "member";
    const opened: OpenedOrgVault = { key, epoch, role };
    // New epoch replaces old (we zeroize previous).
    if (cached) {
      cached.key.fill(0);
    }
    orgKeys.set(orgVaultId, opened);
    return opened;
  }

  async function decryptRowWith(
    key: Uint8Array,
    row: VaultItemRecord,
  ): Promise<DecryptedVaultItem> {
    const plain = await crypto.decryptItem(key, {
      id: row.id,
      vaultId: row.vault_id ?? "",
      envelope: row.envelope ?? "",
      rev: row.rev ?? 0,
    });
    return { id: row.id, rev: row.rev ?? 0, plain };
  }

  async function saveItemWith(
    targetVaultId: string,
    key: Uint8Array,
    plain: VaultItemPlain,
    existing?: { id: string; rev: number },
  ): Promise<string> {
    const dbInstance = drizzle();
    if (!dbInstance) {
      throw new Error("database not ready");
    }
    const id = existing?.id ?? generateId();
    const rev = existing ? existing.rev + 1 : 1;
    const withStamp: VaultItemPlain = { ...plain, updatedAt: nowISO() };
    const protectedItem = await crypto.encryptItem(key, targetVaultId, id, rev, withStamp);
    if (existing) {
      await updateVaultItemMutation(dbInstance, protectedItem);
    } else {
      await createVaultItemMutation(dbInstance, protectedItem);
    }
    return id;
  }

  // `targetVaultId` supplied by caller (currently open vault,
  // personal or org) and forwarded as `scope` of `patchRowVerified` —
  // see isolation note in
  // `@boveda/shared/powersync/verified-write.ts`. Nombrado como
  // `saveItemWith`, not `vaultId`: coexists with `vaultId()`
  // (personal) from context itself; identical name would shadow it.
  async function deleteItem(targetVaultId: string, id: string): Promise<void> {
    const dbInstance = drizzle();
    if (dbInstance) {
      await deleteVaultItemMutation(dbInstance, id, targetVaultId);
    }
  }

  async function restoreItem(targetVaultId: string, id: string): Promise<void> {
    const dbInstance = drizzle();
    if (dbInstance) {
      await restoreVaultItemMutation(dbInstance, id, targetVaultId);
    }
  }

  async function attachFile(
    itemId: string,
    filename: string,
    mime: string,
    bytes: Uint8Array,
  ): Promise<string> {
    const dbInstance = drizzle();
    const vk = vaultKey();
    const vid = vaultId();
    if (!dbInstance || !vk || !vid) {
      throw new Error("vault is locked");
    }
    const attachmentId = generateId();
    const meta: AttachmentMeta = { filename, mime, createdAt: nowISO() };
    const protectedAtt: ProtectedAttachment = await crypto.encryptAttachment({
      vaultKey: vk,
      vaultId: vid,
      itemId,
      attachmentId,
      bytes,
      meta,
    });
    await insertVaultAttachmentMutation(dbInstance, protectedAtt);
    return attachmentId;
  }

  async function openAttachment(row: VaultAttachmentRecord): Promise<crypto.DecryptedAttachment> {
    const vk = vaultKey();
    if (!vk) {
      throw new Error("vault is locked");
    }
    return crypto.decryptAttachment(vk, {
      id: row.id,
      vaultId: row.vault_id ?? "",
      itemId: row.item_id ?? "",
      wrappedKey: row.wrapped_key ?? "",
      metaEnvelope: row.meta_envelope ?? "",
      blob: row.blob ?? "",
      size: row.size ?? 0,
    });
  }

  async function deleteAttachment(targetVaultId: string, id: string): Promise<void> {
    const dbInstance = drizzle();
    if (dbInstance) {
      await deleteVaultAttachmentMutation(dbInstance, id, targetVaultId);
    }
  }

  const value: VaultContextValue = {
    status,
    userId,
    vaultId,
    account: unlocked,
    setup,
    unlock,
    lock,
    decryptRow,
    saveItem,
    openOrgVault,
    decryptRowWith,
    saveItemWith,
    deleteItem,
    restoreItem,
    attachFile,
    openAttachment,
    deleteAttachment,
    keepAlive,
    autoLockMinutes,
    setAutoLockMinutes,
    drizzle,
  };

  return <VaultContext.Provider value={value}>{props.children}</VaultContext.Provider>;
};

export function useVault(): VaultContextValue {
  const ctx = useContext(VaultContext);
  if (!ctx) {
    throw new Error("useVault must be used within <VaultProvider>");
  }
  return ctx;
}
